import React, { useState, useCallback } from "react";
import { Modal, Typography, Button, Card, HighlightCards } from "@ann/web-components";
import {
  ModalContentContainer,
  CardContainer,
  FirstCardHeader,
  FirstCardLeftColumn,
  FirstCardRightColumn,
  SecondCardContent,
  SecondCardLeftColumn,
  SecondCardRightColumn,
  HighlightCardsContainer,
  HighlightCardColumn,
  PermissionsCardContainer,
  PermissionsHeader,
  PermissionsHeaderActions,
  PermissionsContentWrapper,
  PermissionItem,
} from "./CreateViewDetailsModal.styled";
import { ExpandableContentCard } from "../../../Roles/components";

// Mock data for permissions categories
const PERMISSIONS_MOCK_DATA = [
  {
    id: "agents",
    title: "Agents",
    subtitle: "4 permissions · 3 active, 1 disabled",
    icon: "person",
    defaultExpanded: true,
    permissions: [
      { name: "Edit Knowledge", description: "· Modify agent responses and learning content. (Rooftop Elevator)" },
      { name: "Duplicate Agents", description: "· Create copies of existing agents to reuse configurations. (Department)" },
      { name: "Delete Agents", description: "· Remove agents permanently from the workspace. (None)" },
    ],
  },
  {
    id: "automation",
    title: "Automation",
    subtitle: "3 permissions · 3 active",
    icon: "person",
    defaultExpanded: false,
    permissions: [
      { name: "Create Workflows", description: "· Build automated workflows for tasks. (All Departments)" },
      { name: "Edit Workflows", description: "· Modify existing automation rules. (Department)" },
      { name: "Run Automations", description: "· Execute automated processes manually. (Rooftop Elevator)" },
    ],
  },
  {
    id: "data",
    title: "Data",
    subtitle: "3 permissions · 3 active, 1 disabled",
    icon: "person",
    defaultExpanded: false,
    permissions: [
      { name: "Export Data", description: "· Download data in various formats. (All Departments)" },
      { name: "Import Data", description: "· Upload and process external data files. (Department)" },
      { name: "View Reports", description: "· Access analytics and reporting dashboards. (Rooftop Elevator)" },
    ],
  },
];

const CreateViewDetailsModal = ({ isOpen, onClose, roleData }) => {
  // Initialize expanded state based on defaultExpanded values
  const [expandedCards, setExpandedCards] = useState(() => {
    const initial = {};
    PERMISSIONS_MOCK_DATA.forEach((category) => {
      initial[category.id] = category.defaultExpanded;
    });
    return initial;
  });

  const allExpanded = PERMISSIONS_MOCK_DATA.every((category) => expandedCards[category.id]);

  const handleExpandAll = useCallback(() => {
    setExpandedCards((prev) => {
      const newState = {};
      const shouldExpand = !allExpanded;
      PERMISSIONS_MOCK_DATA.forEach((category) => {
        newState[category.id] = shouldExpand;
      });
      return newState;
    });
  }, [allExpanded]);

  const handleToggleCard = useCallback((categoryId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  }, []);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="medium" width="800px">
      <Modal.Body>
        <Card size="medium">
          <ModalContentContainer>
            <FirstCardHeader>
              <FirstCardLeftColumn>
                <Typography category="label" size="medium" weight="bold">
                  Role Information
                </Typography>
              </FirstCardLeftColumn>
              <FirstCardRightColumn>
                <Button 
                  label="Use Template" 
                  size="small" 
                  onClick={onClose}
                />
              </FirstCardRightColumn>
            </FirstCardHeader>

            <CardContainer>
              <Card size="medium">
                <SecondCardContent>
                  <SecondCardLeftColumn>
                    <Typography category="body" size="medium" weight="bold">
                      Role Name
                    </Typography>
                    <Typography category="body" size="medium" weight="regular" color="greyFour">
                      Sales Manager
                    </Typography>
                  </SecondCardLeftColumn>
                  <SecondCardRightColumn>
                    <Typography category="body" size="medium" weight="bold">
                      Role description
                    </Typography>
                    <Typography category="body" size="medium" weight="regular" color="greyFour">
                      Grants access to leads, pipelines, and sales performance dashboards to oversee revenue growth.
                    </Typography>
                  </SecondCardRightColumn>
                </SecondCardContent>
              </Card>

              <HighlightCardsContainer>
                <HighlightCardColumn>
                  <HighlightCards
                    color="body"
                    feedback=""
                    icon="person"
                    iconBgColor="Primary"
                    iconColor="Primary"
                    iconSize="medium"
                    label="Total Permissions"
                    showIcon
                    unit=""
                    value="1,234"
                  />
                </HighlightCardColumn>
                <HighlightCardColumn>
                  <HighlightCards
                    color="body"
                    feedback=""
                    icon="person"
                    iconBgColor="Primary"
                    iconColor="Primary"
                    iconSize="medium"
                    label="Creation date"
                    showIcon
                    unit=""
                    value="1"
                  />
                </HighlightCardColumn>
                <HighlightCardColumn>
                  <HighlightCards
                    color="body"
                    feedback=""
                    icon="person"
                    iconBgColor="Primary"
                    iconColor="Primary"
                    iconSize="medium"
                    label="Modules with permissions"
                    showIcon
                    unit=""
                    value="1,234"
                  />
                </HighlightCardColumn>
              </HighlightCardsContainer>
            </CardContainer>

            <Card size="medium">
              <PermissionsCardContainer>
                <PermissionsHeader>
                  <Typography category="subtitle" size="small" weight="bold">
                    Permissions overview
                  </Typography>
                  <Button
                    label="Edit permissions"
                    size="small"
                    type="primary"
                  />
                </PermissionsHeader>
                <PermissionsHeaderActions>
                  <Button
                    label="View all the details"
                    size="small"
                    type="secondary"
                  />
                  <Button
                    label={allExpanded ? "Collapse all" : "Expand all"}
                    size="small"
                    type="tertiary"
                    onClick={handleExpandAll}
                    disableEffects={true}
                  />
                </PermissionsHeaderActions>
                <PermissionsContentWrapper>
                  {PERMISSIONS_MOCK_DATA.map((category) => (
                    <ExpandableContentCard
                      key={category.id}
                      title={category.title}
                      subtitle={category.subtitle}
                      icon={category.icon}
                      isExpanded={expandedCards[category.id]}
                      onToggle={() => handleToggleCard(category.id)}
                    >
                      {category.permissions.map((permission, index) => (
                        <PermissionItem key={index}>
                          <Typography category="body" size="small" weight="regular">
                            {permission.name}
                          </Typography>
                          <Typography category="body" size="small" weight="regular" color="body">
                            {permission.description}
                          </Typography>
                        </PermissionItem>
                      ))}
                    </ExpandableContentCard>
                  ))}
                </PermissionsContentWrapper>
              </PermissionsCardContainer>
            </Card>
          </ModalContentContainer>
        </Card>
      </Modal.Body>
    </Modal>
  );
};

export default CreateViewDetailsModal;

